import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    Shield,
    Check,
    CreditCard,
    Clock,
    Loader2,
    ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { getSubscriptionStatus, getSubscriptionTiers, startTrial, getCheckoutStatus } from '../lib/api';

const TIER_FEATURES = {
    essential: {
        tagline: "Know what's happening with your computers.",
        features: [
            'Device inventory',
            'Basic health monitoring',
            'Online/offline status',
            'Manual alerts',
            'Basic reporting',
            'Password breach checks',
        ],
    },
    intelligence: {
        tagline: 'Everything in Essential +',
        features: [
            'AI disk scoring for devices',
            'Security summaries',
            'Anomaly detection',
            'Compliance reports',
            'Active failure warnings',
        ],
    },
    protection: {
        tagline: 'Everything in AI Intelligence +',
        features: [
            'Automated threat response',
            'Auto password enforcement',
            'Autoremediation scripts',
            'Ransomware detection',
            'Priority support',
        ],
    },
};

export const StartTrialPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [tiers, setTiers] = useState([]);
    const [selectedTier, setSelectedTier] = useState('essential');
    const [processing, setProcessing] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        checkExistingSubscription();
        fetchTiers();
    }, []);

    useEffect(() => {
        const cancelled = searchParams.get('cancelled');
        const success = searchParams.get('success');
        const sessionId = searchParams.get('session_id');
        if (cancelled === 'true') {
            toast.info('Checkout was cancelled. You can try again when ready.');
            return;
        }
        if (success === '1' && sessionId) {
            pollPaymentStatus(sessionId);
        }
    }, [searchParams]);

    const pollPaymentStatus = async (sessionId) => {
        setLoading(true);
        const maxAttempts = 20;
        for (let i = 0; i < maxAttempts; i++) {
            try {
                const res = await getCheckoutStatus(sessionId);
                const data = res.data || res;
                if (data.payment_status === 'paid' || data.status === 'complete') {
                    toast.success('Trial started. Taking you to the dashboard.');
                    navigate('/dashboard', { replace: true });
                    return;
                }
            } catch (e) {
                console.error('Checkout status check failed:', e);
                toast.error('Error verifying payment status. Retrying...');
            }
            await new Promise((r) => setTimeout(r, 1500));
        }
        setLoading(false);
        toast.error('We’re still confirming your payment. Refresh the page or open the dashboard in a moment.');
    };

    const checkExistingSubscription = async () => {
        try {
            const response = await getSubscriptionStatus();
            const data = response.data || response;
            if (data.status === 'active' || data.status === 'trial' || data.has_payment_method) {
                navigate('/dashboard');
                return;
            }
        } catch (err) {
            console.error('Error checking subscription:', err);
            toast.error('Failed to check subscription status');
        } finally {
            setLoading(false);
        }
    };

    const fetchTiers = async () => {
        try {
            const response = await getSubscriptionTiers();
            const list = response.data?.tiers || response?.tiers || [];
            setTiers(list);
            if (list.length && !list.find((t) => t.id === selectedTier)) {
                setSelectedTier(list[0].id);
            }
        } catch (err) {
            console.error('Error fetching tiers:', err);
            toast.error('Failed to load pricing tiers');
            setTiers([
                { id: 'essential', name: 'Essential Visibility', price: 149 },
                { id: 'intelligence', name: 'AI Intelligence', price: 299 },
                { id: 'protection', name: 'Autonomous Protection', price: 499 },
            ]);
        }
    };

    const handleStartTrial = async () => {
        setProcessing(true);
        try {
            const response = await startTrial({
                tier: selectedTier,
                origin_url: window.location.origin,
            });
            const url = response.data?.url || response?.url;
            if (url) {
                window.location.href = url;
                return;
            }
            toast.error('No checkout URL received. Please try again.');
        } catch (err) {
            const status = err.response?.status;
            const detail = err.response?.data?.detail;
            let message = 'Failed to start trial. Please try again.';
            if (status === 401) {
                message = 'Your session expired. Please log in again.';
            } else if (status === 503) {
                message = 'Payment system isn’t configured. Contact support or try again later.';
            } else if (detail) {
                const raw = Array.isArray(detail) ? (detail[0]?.msg || detail[0]) : detail;
                const lower = String(raw).toLowerCase();
                if (lower.includes('token') || lower.includes('api key') || lower.includes('credentials') || lower.includes('stripe')) {
                    message = 'Payment setup issue. Try logging in again, or contact support if this continues.';
                } else {
                    message = raw;
                }
            } else if (err.message) {
                message = err.message;
            }
            toast.error(message);
        } finally {
            setProcessing(false);
        }
    };

    const selectedTierInfo = tiers.find((t) => t.id === selectedTier) || {
        id: 'essential',
        name: 'Essential Visibility',
        price: 149,
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="start-trial-page">
            <div className="border-b bg-white">
                <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Shield className="w-7 h-7 text-primary" />
                        <span className="font-heading text-xl font-bold tracking-tight">SecureSight</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                        Welcome, {user?.name}
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-4 py-12">
                <div className="text-center mb-10">
                    <Badge className="mb-4 bg-green-100 text-green-800 px-4 py-1">
                        <Clock className="w-3 h-3 mr-1" />
                        10-Day Free Trial
                    </Badge>
                    <h1 className="font-heading text-4xl font-bold tracking-tight mb-4">
                        Choose Your Package
                    </h1>
                    <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                        Select a plan below. Each includes a 10-day free trial. You won&apos;t be charged until the trial ends.
                    </p>
                </div>

                {/* Package cards with features */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    {(['essential', 'intelligence', 'protection']).map((tierId) => {
                        const tier = tiers.find((t) => t.id === tierId) || {
                            id: tierId,
                            name: tierId === 'essential' ? 'Essential Visibility' : tierId === 'intelligence' ? 'AI Intelligence' : 'Autonomous Protection',
                            price: tierId === 'essential' ? 149 : tierId === 'intelligence' ? 299 : 499,
                        };
                        const features = TIER_FEATURES[tierId] || TIER_FEATURES.essential;
                        const isSelected = selectedTier === tierId;
                        const badge =
                            tierId === 'intelligence' ? (
                                <Badge className="mb-2 bg-amber-100 text-amber-800 border-amber-300">MOST POPULAR</Badge>
                            ) : tierId === 'protection' ? (
                                <Badge variant="outline" className="mb-2 bg-red-50 text-red-700 border-red-200">TIER 3</Badge>
                            ) : (
                                <Badge variant="outline" className="mb-2">TIER 1</Badge>
                            );
                        return (
                            <Card
                                key={tierId}
                                className={`cursor-pointer transition-all hover:shadow-lg ${isSelected ? 'ring-2 ring-slate-500 dark:ring-slate-300 border-2 border-slate-500 dark:border-slate-300 shadow-md' : 'bg-card'}`}
                                onClick={() => setSelectedTier(tierId)}
                            >
                                <CardContent className="p-5">
                                    <div className="text-center mb-4">
                                        {badge}
                                        <p className="font-bold text-lg text-foreground">{tier.name}</p>
                                        <p className="text-3xl font-bold mt-2 text-foreground">
                                            ${tier.price}
                                            <span className="text-sm font-normal text-muted-foreground">/mo</span>
                                        </p>
                                    </div>
                                    <p className="text-sm text-muted-foreground text-center mb-4 italic">
                                        {features.tagline}
                                    </p>
                                    <ul className="space-y-2 text-sm text-foreground">
                                        {features.features.map((f, i) => (
                                            <li key={i} className="flex items-center gap-2">
                                                <Check className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                                                <span>{f}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>

                {/* Summary and CTA */}
                <Card className="mb-8 border-border shadow-lg">
                    <CardContent className="p-6 text-center">
                        <p className="text-muted-foreground mb-2">
                            Your selection: <strong>{selectedTierInfo.name}</strong> — ${selectedTierInfo.price}/month after trial
                        </p>
                        <Button
                            size="lg"
                            className="px-8 py-6 text-lg"
                            onClick={handleStartTrial}
                            disabled={processing}
                            data-testid="start-trial-btn"
                        >
                            {processing ? (
                                <>
                                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                    Processing...
                                </>
                            ) : (
                                <>
                                    <CreditCard className="w-5 h-5 mr-2" />
                                    Start Free Trial
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </>
                            )}
                        </Button>
                        <p className="text-sm text-muted-foreground mt-4">
                            <CreditCard className="w-4 h-4 inline mr-1" />
                            Card required • Cancel anytime • Charged ${selectedTierInfo.price}/mo after trial
                        </p>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};
